import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Autocomplete, Chip, Alert, FormControlLabel, Checkbox, Typography
} from '@mui/material';
import { friendsApi, friendGroupsApi } from '../../services/api';
import LoadingButton from '../LoadingButton';
import { normalizeEmail } from '../../utils/splitMath';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const personLabel = (person) => (person.name ? `${person.name} (${person.email})` : person.email);

// Create a group, or edit one (rename, add and remove members) when `group` is passed.
// Members can be picked from your friends or typed in as new emails.
function GroupDialog({ open, group = null, friends, me, onClose, onSaved }) {
  const isEdit = !!group;
  const meEmail = normalizeEmail(me?.email);
  const [name, setName] = useState('');
  const [members, setMembers] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [addAsFriends, setAddAsFriends] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(group ? group.name : '');
    setMembers(group
      ? group.members
        .filter(m => normalizeEmail(m.email) !== meEmail)
        .map(m => ({ email: normalizeEmail(m.email), name: m.name, existing: true }))
      : []);
    setInputValue('');
    setAddAsFriends(true);
    setError('');
  }, [open, group, meEmail]);

  const friendEmails = new Set(friends.map(f => normalizeEmail(f.email)));
  const options = friends
    .map(f => ({ email: normalizeEmail(f.email), name: f.name }))
    .filter(f => f.email !== meEmail);
  const newPeople = members.filter(m => !m.existing && !friendEmails.has(m.email));
  // Only the creator can remove other people (backend rule)
  const canRemoveExisting = !isEdit || group.isCreator;

  // Turn typed text (one or more emails, separated by commas/spaces) into members
  const addTyped = (text, current) => {
    const parts = text.split(/[\s,;]+/).map(normalizeEmail).filter(Boolean);
    const next = [...current];
    const invalid = [];
    for (const email of parts) {
      if (!EMAIL_REGEX.test(email)) {
        invalid.push(email);
      } else if (email !== meEmail && !next.some(m => m.email === email)) {
        const friend = options.find(o => o.email === email);
        next.push({ email, name: friend ? friend.name : null, existing: false });
      }
    }
    setError(invalid.length ? `Not a valid email: ${invalid.join(', ')}` : '');
    return { next, invalid };
  };

  const handleChange = (_, newValue) => {
    let next = [];
    for (const item of newValue) {
      if (typeof item === 'string') {
        next = addTyped(item, next).next;
      } else if (!next.some(m => m.email === item.email)) {
        const previous = members.find(m => m.email === item.email);
        next.push(previous || { ...item, existing: false });
      }
    }
    // Members that can't be removed stay put
    if (!canRemoveExisting) {
      for (const m of members.filter(m => m.existing)) {
        if (!next.some(n => n.email === m.email)) next.unshift(m);
      }
    }
    setMembers(next);
  };

  const handleSave = async () => {
    // Pick up an email that was typed but not yet turned into a chip
    let finalMembers = members;
    if (inputValue.trim()) {
      const { next, invalid } = addTyped(inputValue, members);
      if (invalid.length) return;
      finalMembers = next;
      setMembers(next);
      setInputValue('');
    }
    if (!name.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (isEdit) {
        if (name.trim() !== group.name) {
          await friendGroupsApi.update(group.id, { name: name.trim() });
        }
        const added = finalMembers.filter(m => !m.existing).map(m => m.email);
        if (added.length) {
          await friendGroupsApi.addMembers(group.id, added);
        }
        const removed = group.members
          .map(m => normalizeEmail(m.email))
          .filter(email => email !== meEmail && !finalMembers.some(m => m.email === email));
        for (const email of removed) {
          await friendGroupsApi.removeMember(group.id, email);
        }
      } else {
        await friendGroupsApi.create({ name: name.trim(), memberEmails: finalMembers.map(m => m.email) });
      }

      if (addAsFriends) {
        const toBefriend = finalMembers.filter(m => !m.existing && !friendEmails.has(m.email));
        // Best effort: the group is already saved if one of these fails
        await Promise.allSettled(toBefriend.map(m => friendsApi.add(m.email)));
      }

      await onSaved(isEdit ? 'Group updated' : 'Group created');
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || (isEdit ? 'Failed to update group' : 'Failed to create group'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Group' : 'Create Group'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Group Name"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Trip to Goa, Roommates"
          />

          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            options={options}
            value={members}
            inputValue={inputValue}
            onInputChange={(_, value, reason) => {
              // Typing a comma or space after an email turns it into a chip
              if (reason === 'input' && /[\s,;]$/.test(value) && value.trim()) {
                setMembers(addTyped(value, members).next);
                setInputValue('');
              } else {
                setInputValue(value);
              }
            }}
            onChange={handleChange}
            getOptionLabel={(option) => (typeof option === 'string' ? option : personLabel(option))}
            isOptionEqualToValue={(option, value) => option.email === value.email}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => {
                const { key, onDelete, ...itemProps } = getItemProps({ index });
                const locked = option.existing && !canRemoveExisting;
                return (
                  <Chip
                    key={key}
                    size="small"
                    label={option.name || option.email}
                    variant={friendEmails.has(option.email) || option.existing ? 'filled' : 'outlined'}
                    {...itemProps}
                    onDelete={locked ? undefined : onDelete}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Members"
                placeholder={members.length ? '' : 'Pick friends or type an email'}
                helperText={isEdit
                  ? `You are a member.${canRemoveExisting ? '' : ' Only the group creator can remove people.'}`
                  : 'You are added automatically. Pick from your friends or type any email and press Enter.'}
              />
            )}
          />

          {newPeople.length > 0 && (
            <Stack spacing={0.5}>
              <FormControlLabel
                control={<Checkbox checked={addAsFriends} onChange={(e) => setAddAsFriends(e.target.checked)} />}
                label="Also add new people to my friends"
              />
              <Typography variant="caption" color="text.secondary">
                New: {newPeople.map(p => p.email).join(', ')}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton onClick={handleSave} variant="contained" loading={saving} disabled={!name.trim()}>
          {isEdit ? 'Save Changes' : 'Create Group'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default GroupDialog;
